/**
 * The route table: one entry per screen. The sidebar, the route guard, the page title
 * and the contextual Help link are all derived from it.
 *
 * To add a screen (module helpers E3–E5): write the page in src/app/pages/<module>/,
 * then point its entry's `page` at it (lazy import). Keep `perm` in step with the API.
 */
import {
  ArrowLeftRight,
  BadgeCheck,
  Bell,
  Banknote,
  Building2,
  ClipboardCheck,
  CircleHelp,
  FileBarChart,
  FolderTree,
  Gauge,
  History,
  KeyRound,
  Landmark,
  BookOpenCheck,
  Gavel,
  Calculator,
  Coins,
  BadgeDollarSign,
  Vault,
  ChartColumn,
  MonitorPlay,
  Ticket,
  LayoutDashboard,
  Package,
  PackagePlus,
  Percent,
  Receipt,
  ScanLine,
  Settings,
  Tag,
  Users,
  UserSquare2,
  type LucideIcon,
} from 'lucide-react';
import { lazy, type ComponentType, type LazyExoticComponent } from 'react';
import { P } from '../lib/permissions';

export type GroupId = 'inventory' | 'pawn' | 'sales' | 'fx' | 'audits' | 'admin';

export interface NavGroup {
  id: GroupId;
  label: string;
  icon: LucideIcon;
}

export const GROUPS: NavGroup[] = [
  { id: 'inventory', label: 'Inventory', icon: Package },
  { id: 'pawn', label: 'Pawn', icon: Landmark },
  { id: 'sales', label: 'Sales', icon: Receipt },
  { id: 'fx', label: 'Money exchange', icon: Coins },
  { id: 'audits', label: 'Audits', icon: ClipboardCheck },
  { id: 'admin', label: 'Admin', icon: Settings },
];

export interface RouteDef {
  path: string;
  title: string;
  icon: LucideIcon;
  /** Sidebar placement: a group id, 'top' for a top-level link, or null (not in the menu). */
  nav: GroupId | 'top' | null;
  /** Any of these permissions opens the screen (empty = any signed-in user). */
  perm: string[];
  /** Help slug for the page header link. */
  help?: string;
  page: ComponentType | LazyExoticComponent<ComponentType>;
}

const Dashboard = lazy(() => import('./pages/Dashboard'));
const Items = lazy(() => import('./pages/items/ItemsPage'));
const ItemDetail = lazy(() => import('./pages/items/ItemDetail'));
const ItemForm = lazy(() => import('./pages/items/ItemForm'));
const Intake = lazy(() => import('./pages/items/Intake'));
const IntakeBatch = lazy(() => import('./pages/items/IntakeBatch'));
const Categories = lazy(() => import('./pages/items/Categories'));
const Labels = lazy(() => import('./pages/items/Labels'));
const Rates = lazy(() => import('./pages/items/Rates'));
const ScanPage = lazy(() => import('./pages/ScanPage'));
const PawnTickets = lazy(() => import('./pages/pawn/TicketsPage'));
const PawnNew = lazy(() => import('./pages/pawn/NewTicket'));
const PawnTicket = lazy(() => import('./pages/pawn/TicketDetail'));
const PawnCashBook = lazy(() => import('./pages/pawn/CashBook'));
const PawnForfeitures = lazy(() => import('./pages/pawn/Forfeitures'));
const PawnSchemes = lazy(() => import('./pages/pawn/Schemes'));
const PawnReports = lazy(() => import('./pages/pawn/PawnReports'));
const Customers = lazy(() => import('./pages/customers/CustomersPage'));
const CustomerDetail = lazy(() => import('./pages/customers/CustomerDetail'));
const CustomerForm = lazy(() => import('./pages/customers/CustomerForm'));
const HelpIndex = lazy(() => import('./help/HelpIndex'));
const AuditsPage = lazy(() => import('./pages/audits/AuditsPage'));
const AuditDetail = lazy(() => import('./pages/audits/AuditDetail'));
const AuditCount = lazy(() => import('./pages/audits/AuditCount'));
const AuditTrends = lazy(() => import('./pages/audits/AuditTrends'));
const AlertsPage = lazy(() => import('./pages/control/AlertsPage'));
const ReportsPage = lazy(() => import('./pages/control/ReportsPage'));
const UsersPage = lazy(() => import('./pages/admin/UsersPage'));
const RolesPage = lazy(() => import('./pages/admin/RolesPage'));
const BranchesPage = lazy(() => import('./pages/admin/BranchesPage'));
const SettingsPage = lazy(() => import('./pages/admin/SettingsPage'));
const ActivityPage = lazy(() => import('./pages/admin/ActivityPage'));
const ApprovalsPage = lazy(() => import('./pages/admin/ApprovalsPage'));
const Transfers = lazy(() => import('./pages/transfers/TransfersPage'));
const NewTransfer = lazy(() => import('./pages/transfers/NewTransfer'));
const TransferDetail = lazy(() => import('./pages/transfers/TransferDetail'));
const Discrepancies = lazy(() => import('./pages/transfers/Discrepancies'));
const Sales = lazy(() => import('./pages/sales/SalesPage'));
const NewSale = lazy(() => import('./pages/sales/NewSale'));
const SaleDetail = lazy(() => import('./pages/sales/SaleDetail'));
const CashUpPage = lazy(() => import('./pages/sales/CashUp'));
const FxNew = lazy(() => import('./pages/fx/NewExchange'));
const FxTransactions = lazy(() => import('./pages/fx/FxTransactions'));
const FxDetail = lazy(() => import('./pages/fx/FxDetail'));
const FxRates = lazy(() => import('./pages/fx/RateBoard'));
const FxStock = lazy(() => import('./pages/fx/FxStock'));
const FxReports = lazy(() => import('./pages/fx/FxReports'));

export const ROUTES: RouteDef[] = [
  { path: '/', title: 'Dashboard', icon: LayoutDashboard, nav: 'top', perm: [P.dashboard], help: 'getting-started', page: Dashboard },

  { path: '/inventory/items', title: 'Items', icon: Package, nav: 'inventory', perm: [P.itemsView], help: 'tagging', page: Items },
  { path: '/inventory/items/new', title: 'New item', icon: PackagePlus, nav: null, perm: [P.itemsCreate], help: 'tagging', page: ItemForm },
  { path: '/inventory/items/:id', title: 'Item', icon: Package, nav: null, perm: [P.itemsView, P.pawnView], help: 'tagging', page: ItemDetail },
  { path: '/inventory/items/:id/edit', title: 'Edit item', icon: Package, nav: null, perm: [P.itemsEdit], help: 'tagging', page: ItemForm },
  { path: '/inventory/intake/:id', title: 'Intake batch', icon: PackagePlus, nav: null, perm: [P.itemsCreate, P.itemsImport], help: 'intake', page: IntakeBatch },
  {
    path: '/inventory/intake',
    title: 'Intake',
    icon: PackagePlus,
    nav: 'inventory',
    perm: [P.itemsCreate, P.itemsImport],
    help: 'intake',
    page: Intake,
  },
  {
    path: '/inventory/categories',
    title: 'Categories',
    icon: FolderTree,
    nav: 'inventory',
    perm: [P.categories],
    help: 'categories',
    page: Categories,
  },
  {
    path: '/inventory/labels',
    title: 'Labels',
    icon: Tag,
    nav: 'inventory',
    perm: [P.labels],
    help: 'labels',
    page: Labels,
  },

  { path: '/transfers', title: 'Transfers', icon: ArrowLeftRight, nav: 'top', perm: [P.transfersView], help: 'transfers', page: Transfers },
  { path: '/transfers/new', title: 'New transfer', icon: ArrowLeftRight, nav: null, perm: ['transfers.request'], help: 'transfers', page: NewTransfer },
  { path: '/transfers/discrepancies', title: 'Transfer discrepancies', icon: ArrowLeftRight, nav: null, perm: [P.transfersView], help: 'transfers', page: Discrepancies },
  { path: '/transfers/:id', title: 'Transfer', icon: ArrowLeftRight, nav: null, perm: [P.transfersView], help: 'transfers', page: TransferDetail },

  {
    path: '/pawn',
    title: 'Tickets',
    icon: Ticket,
    nav: 'pawn',
    perm: [P.pawnView],
    help: 'pawn',
    page: PawnTickets,
  },
  {
    path: '/pawn/cash-book',
    title: 'Cash book',
    icon: BookOpenCheck,
    nav: 'pawn',
    perm: [P.cashbook],
    help: 'cash-book',
    page: PawnCashBook,
  },
  {
    path: '/pawn/forfeitures',
    title: 'Forfeitures',
    icon: Gavel,
    nav: 'pawn',
    perm: [P.pawnForfeit],
    help: 'forfeiture',
    page: PawnForfeitures,
  },
  {
    path: '/pawn/schemes',
    title: 'Interest schemes',
    icon: Calculator,
    nav: 'pawn',
    perm: [P.pawnSchemes],
    help: 'interest',
    page: PawnSchemes,
  },

  { path: '/pawn/new', title: 'New pawn ticket', icon: Ticket, nav: null, perm: [P.pawnCreate], help: 'pawn', page: PawnNew },
  { path: '/pawn/tickets/:id', title: 'Pawn ticket', icon: Ticket, nav: null, perm: [P.pawnView], help: 'pawn-renew-redeem', page: PawnTicket },
  { path: '/pawn/reports', title: 'Pawn reports', icon: FileBarChart, nav: 'pawn', perm: ['pawn.reports'], help: 'pawn', page: PawnReports },
  { path: '/customers/new', title: 'New customer', icon: UserSquare2, nav: null, perm: ['customers.manage'], help: 'customers', page: CustomerForm },
  { path: '/customers/:id', title: 'Customer', icon: UserSquare2, nav: null, perm: [P.customers], help: 'customers', page: CustomerDetail },
  { path: '/customers/:id/edit', title: 'Edit customer', icon: UserSquare2, nav: null, perm: ['customers.manage'], help: 'customers', page: CustomerForm },
  { path: '/sales', title: 'Sales', icon: Receipt, nav: 'sales', perm: [P.salesView, P.salesRecord], help: 'sales', page: Sales },
  { path: '/sales/new', title: 'New sale', icon: Receipt, nav: null, perm: [P.salesRecord], help: 'sales', page: NewSale },
  { path: '/sales/cash-up', title: 'Cash-up', icon: Banknote, nav: 'sales', perm: [P.cashup, P.cashupSignoff], help: 'cash-up', page: CashUpPage },
  { path: '/sales/:id', title: 'Sale', icon: Receipt, nav: null, perm: [P.salesView, P.salesRecord], help: 'sales', page: SaleDetail },
  {
    path: '/customers',
    title: 'Customers',
    icon: UserSquare2,
    nav: 'sales',
    perm: [P.customers],
    help: 'customers',
    page: Customers,
  },

  { path: '/fx/new', title: 'New exchange', icon: BadgeDollarSign, nav: 'fx', perm: [P.fxTransact], help: 'money-exchange', page: FxNew },
  { path: '/fx/transactions', title: 'Exchanges', icon: Coins, nav: 'fx', perm: [P.fxView], help: 'money-exchange', page: FxTransactions },
  { path: '/fx/transactions/:id', title: 'Exchange', icon: Coins, nav: null, perm: [P.fxView], help: 'money-exchange', page: FxDetail },
  { path: '/fx/rates', title: 'Rate board', icon: MonitorPlay, nav: 'fx', perm: [P.fxView], help: 'money-exchange', page: FxRates },
  { path: '/fx/stock', title: 'Currency stock', icon: Vault, nav: 'fx', perm: [P.fxView], help: 'money-exchange', page: FxStock },
  { path: '/fx/reports', title: 'Exchange reports', icon: ChartColumn, nav: 'fx', perm: [P.fxView], help: 'money-exchange', page: FxReports },

  {
    path: '/audits',
    title: 'Audits',
    icon: ClipboardCheck,
    nav: 'audits',
    perm: [P.auditsView, P.auditsCount],
    help: 'audit',
    page: AuditsPage,
  },
  { path: '/audits/trends', title: 'Audit trends', icon: ClipboardCheck, nav: null, perm: [P.auditsView], help: 'audit', page: AuditTrends },
  { path: '/audits/:id', title: 'Audit', icon: ClipboardCheck, nav: null, perm: [P.auditsView, P.auditsCount], help: 'audit', page: AuditDetail },
  { path: '/audits/:id/count', title: 'Count', icon: ScanLine, nav: null, perm: [P.auditsCount], help: 'audit', page: AuditCount },
  { path: '/alerts', title: 'Alerts', icon: Bell, nav: null, perm: [P.alerts, P.dashboard], help: 'dashboard', page: AlertsPage },
  { path: '/audits/scan', title: 'Scan', icon: ScanLine, nav: 'audits', perm: [P.auditsCount, P.itemsView], help: 'audit', page: ScanPage },

  {
    path: '/reports',
    title: 'Reports',
    icon: FileBarChart,
    nav: 'top',
    perm: [P.reports],
    help: 'reports',
    page: ReportsPage,
  },

  { path: '/admin/branches', title: 'Branches', icon: Building2, nav: 'admin', perm: [P.branches, P.branchesView], help: 'admin', page: BranchesPage },
  { path: '/admin/users', title: 'Users', icon: Users, nav: 'admin', perm: [P.users], help: 'admin', page: UsersPage },
  { path: '/admin/roles', title: 'Roles', icon: KeyRound, nav: 'admin', perm: [P.roles], help: 'admin', page: RolesPage },
  { path: '/admin/settings', title: 'Settings', icon: Gauge, nav: 'admin', perm: [P.settings, P.activity], help: 'admin', page: SettingsPage },
  { path: '/admin/rates', title: 'Rates', icon: Percent, nav: 'admin', perm: [P.ratesView, P.rates], help: 'categories', page: Rates },
  { path: '/admin/activity', title: 'Activity log', icon: History, nav: 'admin', perm: [P.activity], help: 'admin', page: ActivityPage },
  { path: '/admin/approvals', title: 'Approvals', icon: BadgeCheck, nav: 'admin', perm: [P.approvalsManager, P.approvalsOwner], help: 'admin', page: ApprovalsPage },

  { path: '/help', title: 'Help', icon: CircleHelp, nav: 'top', perm: [], page: HelpIndex },
];

/** Routes that sit outside the menu but inside the shell. */
export const EXTRA_ROUTES: { path: string; page: LazyExoticComponent<ComponentType> }[] = [
  { path: '/help/:slug', page: lazy(() => import('./help/HelpArticle')) },
  { path: '/account', page: lazy(() => import('./auth/Account')) },
];

/** The route whose path is the longest prefix of `pathname`. */
export function routeFor(pathname: string): RouteDef | undefined {
  return [...ROUTES].sort((a, b) => b.path.length - a.path.length).find((r) => (r.path === '/' ? pathname === '/' : pathname === r.path || pathname.startsWith(`${r.path}/`)));
}
